import { createFileRoute, useNavigate, useParams, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useApp } from "@/store/app";
import { useJoinLeague, useLeagueByCode } from "@/hooks/useLeaguesDb";
import { useT } from "@/i18n";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/join/$leagueCode")({
  component: JoinPage,
});

const PENDING_KEY = "pendingLeagueJoin";

function JoinPage() {
  const { leagueCode } = useParams({ from: "/join/$leagueCode" });
  const code = leagueCode.trim().toUpperCase();
  const navigate = useNavigate();
  const t = useT();
  const { user, loading } = useAuth();
  const appUser = useApp((s) => s.user);
  const { data: league, isLoading: leagueLoading, isFetched } = useLeagueByCode(code);
  const joinLeague = useJoinLeague();
  const ranRef = useRef(false);

  useEffect(() => {
    if (loading) return;

    // Not logged in → store pending and redirect to signup (survives email verification)
    if (!user) {
      try {
        localStorage.setItem(PENDING_KEY, code);
      } catch {
        /* ignore */
      }
      navigate({ to: "/signup" });
      return;
    }

    if (!isFetched || ranRef.current) return;

    if (!league) {
      // handled by render below
      return;
    }

    if (!appUser) return;
    ranRef.current = true;

    joinLeague.mutate(
      { userId: appUser.id, code },
      {
        onSuccess: (lg) => {
          try {
            localStorage.removeItem(PENDING_KEY);
          } catch {
            /* ignore */
          }
          toast.success(
            t("You joined {league}!", undefined, "Te-ai alăturat ligii {league}! 🎉").replace(
              "{league}",
              lg.name,
            ),
          );
          navigate({ to: "/leagues/$code", params: { code: lg.code } });
        },
        onError: (e: Error) => {
          toast.error(e.message || t("Could not join league"));
          navigate({ to: "/leagues" });
        },
      },
    );
  }, [loading, user, appUser, league, isFetched, code, navigate, joinLeague, t]);

  if (!loading && user && isFetched && !league) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-background to-accent p-6 text-center">
        <img src={logo} alt="" className="mb-4 h-24 w-24 object-contain" />
        <Trophy className="h-10 w-10 text-muted-foreground" />
        <p className="mt-3 font-semibold">
          {t(
            "This league doesn't exist or the code is invalid.",
            undefined,
            "Această ligă nu există sau codul este greșit.",
          )}
        </p>
        <Link
          to="/leagues"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          {t("Back", undefined, "Înapoi")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-background to-accent p-6">
      <img src={logo} alt="" className="mb-4 h-28 w-28 animate-pulse object-contain" />
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {leagueLoading || joinLeague.isPending
          ? t("Joining league...", undefined, "Se alătură ligii...")
          : t("Loading...", undefined, "Se încarcă...")}
      </div>
    </div>
  );
}
